import { useCallback, useEffect, useState } from "react";
import { Paperclip, Save } from "lucide-react";
import { api } from "@/lib/api";
import { t } from "@/lib/i18n";
import { Card } from "@/components/ui/Card";
import { FormMessage, type Feedback } from "@/components/ui/FormMessage";
import { DEFAULT_PAYLOAD_SETTINGS, type PayloadSettings, type GenericResponse } from "@/lib/types";

const MB = 1024 * 1024;

export function PayloadsPage() {
  const [maxMb, setMaxMb] = useState<number>(DEFAULT_PAYLOAD_SETTINGS.max_bytes / MB);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<Feedback>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get<PayloadSettings>("/api/settings/payloads");
      setMaxMb(Math.max(1, Math.round(data.max_bytes / MB)));
    } catch (e) {
      setFeedback({ kind: "error", text: e instanceof Error ? e.message : t("Laden fehlgeschlagen") });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const onSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setFeedback(null);
    try {
      const resp = await api.post<GenericResponse>("/api/settings/payloads", {
        max_bytes: Math.round(maxMb * MB),
      });
      if (resp.success) {
        setFeedback({ kind: "success", text: resp.message });
      } else {
        setFeedback({ kind: "error", text: resp.message });
      }
    } catch (e) {
      setFeedback({ kind: "error", text: e instanceof Error ? e.message : t("Speichern fehlgeschlagen") });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-4 p-6">
      <FormMessage feedback={feedback} />

      <Card
        title={t("Payload-Anhänge")}
        description={t(
          "Maximale Größe für an Log-Events angehängte Dateien (Dump, Bild, Video, Audio). Gilt für inline-Payloads (TCP) und den REST-Upload.",
        )}
        actions={<Paperclip className="h-4 w-4 text-muted-foreground" aria-hidden="true" />}
      >
        {loading ? (
          <div className="text-sm text-muted-foreground">{t("Lade…")}</div>
        ) : (
          <form onSubmit={onSave} className="flex flex-col gap-4">
            <label className="flex max-w-xs flex-col gap-1">
              <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                {t("Maximale Payload-Größe (MB)")}
              </span>
              <input
                type="number"
                min={1}
                max={256}
                value={maxMb}
                onChange={(e) => setMaxMb(clamp(parseInt(e.target.value, 10) || 1, 1, 256))}
                className="h-9 rounded-md border border-border bg-background px-3 text-sm tabular-nums outline-none focus:border-primary focus:ring-1 focus:ring-primary"
              />
              <span className="text-[11px] text-muted-foreground">
                {t("Payloads über {mb} MB werden abgelehnt (TCP: verworfen, REST: HTTP 413).", { mb: maxMb })}
              </span>
            </label>

            <div>
              <button
                type="submit"
                disabled={saving}
                className="inline-flex h-9 items-center gap-1.5 rounded-md bg-primary px-3 text-xs font-medium text-primary-foreground disabled:opacity-60"
              >
                <Save className="h-3.5 w-3.5" /> {saving ? t("Speichern…") : t("Einstellungen speichern")}
              </button>
            </div>
          </form>
        )}
      </Card>
    </div>
  );
}

function clamp(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, n));
}
